import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Divider,
  Box,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export const INSIGHT_SECTION_HELP_TEXT: Record<string, string> = {
  "KEY TAKEAWAYS":
    "A high-level bullet summary of the most important movements and drivers—your quick snapshot before reading deeper.",
  "OVERALL INSIGHTS":
    "Analyzes performance at the selected filter level from multiple perspectives—such as region, retailer, channel, and brand—to provide deeper insights.",
  "REGIONAL INSIGHTS":
    "Shows how each region performed and highlights clear winners and laggards drilled down to region -> retailer -> brand level. Explains which regions drove growth or decline and what metrics caused it.",
  "RETAILER INSIGHTS":
    "Breaks down performance inside each retailer and compares them within the selected scope. Highlights revenue/share changes and their drivers.",
  "PROMO INSIGHTS":
    "Provides a focused view of sub-brand x retailer promo performance, highlighting revenue shifts, promo effectiveness, execution drivers, and store-level winners and gaps with clear cause-effect insights.",
  "PEPSICO INSIGHTS":
    "Shows how PepsiCo divisions performed vs. competitors. Identifies the brands and packs that drove gains or losses.",
  "COMPETITOR INSIGHTS":
    "Highlights competitor manufacturers and brands that gained/lost vs. PepsiCo and why.",
  "PRICE POINT INSIGHTS":
    "Breaks down performance across price bands and links movements to promo depth, shopper trade-up/down, or distribution.",
  "SUMMARISED INSIGHTS":
    "A consolidated summary of all insight sections, providing a unified overview of key findings across all dimensions.",
  "MARKET INSIGHTS":
    "Appears only when an entity is selected and benchmarks it against peers to show who is driving growth and who is falling behind.",
};

const INSIGHT_SECTION_SEQUENCE = [
  "Key Takeaways",
  "Overall Insights",
  "Regional Insights",
  "Retailer Insights",
  "Promo Insights",
  "PepsiCo Insights",
  "Competitor Insights",
  "Price Point Insights",
  "Summarised Insights",
  "Market Insights",
] as const;

export const getInsightSectionHelpText = (title: string) => {
  const normalizedTitle = title.trim().toUpperCase();
  return INSIGHT_SECTION_HELP_TEXT[normalizedTitle] || "";
};

interface Props {
  open: boolean;
  onClose: () => void;
  title?: string;
}

/* -----------------------------------------
   Reusable Section Header
----------------------------------------- */
const SectionTitle: React.FC<{ number?: string; title: string }> = ({
  number,
  title,
}) => (
  <Box
    display="flex"
    alignItems="center"
    mb={1.2}
    mt={2.4}
    sx={{
      background:
        "linear-gradient(90deg, rgba(108,99,255,0.08) 0%, rgba(90,200,250,0.06) 100%)",
      borderRadius: 1.2,
      px: 1.2,
      py: 0.7,
    }}
  >
    {number && (
      <Chip
        label={number}
        size="small"
        sx={{
          mr: 1,
          fontWeight: 600,
          color: "white",
          background: "linear-gradient(135deg, #6C63FF 0%, #5AC8FA 100%)",
          height: 22,
        }}
      />
    )}
    <Typography
      variant="subtitle1"
      sx={{
        fontWeight: 600,
        background: "linear-gradient(45deg, #6C63FF, #5AC8FA)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        fontSize: "1rem",
      }}
    >
      {title}
    </Typography>
  </Box>
);

const FilterHelp: React.FC<Props> = ({
  open,
  onClose,
  title = "Middle East Insights – Help & How-To Guide",
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 8px 28px rgba(0,0,0,0.15)",
          backgroundColor: "#fafafa",
          height: "85vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* -----------------------------------------
          Header
      ----------------------------------------- */}
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: "1.15rem",
          color: "white",
          py: 1.3,
          pl: 2,
          pr: 5,
          textAlign: "center",
          background: "linear-gradient(90deg, #6C63FF 0%, #5AC8FA 100%)",
          position: "relative",
          letterSpacing: 0.2,
          lineHeight: 1.45,
        }}
      >
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 10,
            top: 10,
            color: "white",
            backgroundColor: "rgba(255,255,255,0.15)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.28)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* -----------------------------------------
          Content
      ----------------------------------------- */}
      <DialogContent
        dividers
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          px: 3,
          py: 2,
          backgroundColor: "#ffffff",
        }}
      >
        <SectionTitle title="This guide walks you through how the dashboard works and how to read the insights." />

        {/* -----------------------------------------
            Section 1
        ----------------------------------------- */}
        <SectionTitle number="1" title="What This Dashboard Is For" />
        <Typography paragraph sx={{ fontSize: "0.95rem", lineHeight: 1.6 }}>
          This dashboard helps you read the story behind Middle East
          performance across Salty Snacks. It brings together revenue, share,
          distribution, pricing, promo impact, merchandising and brand
          performance into one place—so you can quickly spot what changed and
          why it changed.
        </Typography>

        {/* -----------------------------------------
            Section 2
        ----------------------------------------- */}
        <SectionTitle number="2" title="How to Generate Insights" />
        <Typography paragraph sx={{ fontSize: "0.95rem", lineHeight: 1.6 }}>
          Use the filters to define the exact slice of the market you want to
          analyse. Click Generate Insights. The dashboard will create a
          narrative tailored to your selected filters.
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* -----------------------------------------
            Section 3 + Key Takeaways (refined)
        ----------------------------------------- */}
        <SectionTitle number="3" title="What Each Insights Section Means" />

        {INSIGHT_SECTION_SEQUENCE.map((sectionTitle) => (
          <React.Fragment key={sectionTitle}>
            <SectionTitle title={sectionTitle} />
            <Typography paragraph sx={{ fontSize: "0.95rem", lineHeight: 1.6 }}>
              {getInsightSectionHelpText(sectionTitle)}
            </Typography>
          </React.Fragment>
        ))}

        <Divider sx={{ my: 2 }} />

        <Typography
          paragraph
          sx={{ fontSize: "0.9rem", color: "text.secondary" }}
        >
          Note: When a retailer is selected, the category remains filtered by
          the retailer by design.
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default FilterHelp;
